import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CardOnboardingCompany,
  CardOnboardingRepresentation,
  useStartCardOnboarding
} from '../../../../queries/pxtraCard';
import { ContentDialog } from '../dialogs/contentDialog';
import { onboardingErrorMessage } from './errorMessage';

type Props = {
  companyId: string;
  company: CardOnboardingCompany;
  open: boolean;
  onClose: () => void;
};

type Person = { first_name: string; last_name: string; email: string };

const EMPTY: Person = { first_name: '', last_name: '', email: '' };

/**
 * The representation question lives here rather than as its own checklist step:
 * the type of representation and the person are inputs to the create call, so
 * they have to be answered before anything exists at Swan — and the area keeps
 * a single entry point for starting.
 */
export const StartOnboardingDialog: FC<Props> = ({ companyId, company, open, onClose }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [representation, setRepresentation] = useState<CardOnboardingRepresentation>('self');

  // Prefilled from the contact on file, but editable: that contact is often
  // somebody other than the manager signing in, and Swan rejects an empty name.
  const [self, setSelf] = useState<Person>({
    ...EMPTY,
    first_name: company.contact_first_name ?? '',
    last_name: company.contact_last_name ?? ''
  });
  const [other, setOther] = useState<Person>(EMPTY);

  const { mutate: start, isPending } = useStartCardOnboarding(companyId);

  const person = representation === 'self' ? self : other;
  const setPerson = representation === 'self' ? setSelf : setOther;

  const complete =
    !!person.first_name.trim() &&
    !!person.last_name.trim() &&
    (representation === 'self' || !!person.email.trim());

  const submit = () => {
    // Safari blocks a window opened after an await, so the tab is claimed
    // inside the click and only pointed at Swan once the URL comes back.
    const target = window.open('', '_blank');

    start(
      {
        representation,
        accountAdmin: {
          first_name: person.first_name.trim(),
          last_name: person.last_name.trim(),
          ...(representation === 'other' ? { email: person.email.trim() } : {})
        }
      },
      {
        onSuccess: ({ onboarding_url }) => {
          // The dashboard stays open behind Swan's form, so the manager has
          // somewhere to come back to without retyping the URL.
          if (target) {
            target.location.href = onboarding_url;
          } else {
            window.open(onboarding_url, '_blank');
          }

          onClose();
        },
        onError: (error) => {
          target?.close();
          enqueueSnackbar(onboardingErrorMessage(error) ?? t('pxtraCard:onboardingActionFailed'), {
            variant: 'error'
          });
        }
      }
    );
  };

  const field = (key: keyof Person, label: string, type = 'text') => (
    <label className="mt-2 block text-sm">
      <span className="text-grey-600">{label}</span>
      <input
        type={type}
        className="mt-1 box-border w-full rounded-lg border border-solid border-grey-100 px-3 py-2 text-sm"
        value={person[key]}
        onChange={(event) => setPerson({ ...person, [key]: event.target.value })}
      />
    </label>
  );

  return (
    <ContentDialog
      open={open}
      width="520px"
      title={t('pxtraCard:startDialogTitle')}
      onClose={onClose}
      secondaryButton={t('common:CANCEL')}
      onSecondaryButtonClick={onClose}
      primaryButton={t('pxtraCard:startDialogSubmit')}
      primaryButtonDisabled={!complete || isPending}
      onPrimaryButtonClick={submit}
    >
      <div>
        <p className="m-0 text-sm font-medium">{t('pxtraCard:startDialogQuestion')}</p>

        {(['self', 'other'] as const).map((option) => (
          <label key={option} className="mt-2 flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="representation"
              checked={representation === option}
              onChange={() => setRepresentation(option)}
            />
            {t(option === 'self' ? 'pxtraCard:startDialogSelf' : 'pxtraCard:startDialogOther')}
          </label>
        ))}

        <div className="mt-3">
          {field('first_name', t('pxtraCard:startDialogFirstName'))}
          {field('last_name', t('pxtraCard:startDialogLastName'))}
          {representation === 'other' && field('email', t('pxtraCard:startDialogEmail'), 'email')}
        </div>

        <p className="m-0 mt-3 text-xs text-grey-600">
          {representation === 'self'
            ? t('pxtraCard:startDialogSelfHint')
            : t('pxtraCard:startDialogHint')}
        </p>
      </div>
    </ContentDialog>
  );
};
